import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Action_Status_Enum,
  InputMaybe,
  UpdateActionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateActionTRPCInput {
  Id: string;
  Title: string;
  DateDue: string;
  DateRaised: string;
  Status: Action_Status_Enum;
  Priority?: InputMaybe<number>;
  Description?: InputMaybe<string>;
  ClosedDate?: InputMaybe<string>;
  OriginalTimestamp: string;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateActionMutation => {
  return {
    updateChildAction: {
      __typename: 'UpdateChildActionOutput',
      affected_rows: 1,
      change_request_id: null,
    },
  };
};

export const useUpdateActionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.action.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.actionById.queryKey(),
        });
      },
    }),
  });

  return {
    updateAction: async (
      variables: UpdateActionTRPCInput
    ): Promise<UpdateActionMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
